import useEvent from '@lyearn/core/hooks/useEvent';
import { jsonParse } from '@lyearn/core/utils/json';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { GenerativeTaskFragment } from '@/components/LyearnAI/graphql/fragments/generated/GenerativeTaskFragment';
import useGenerativeTask from '@/components/LyearnAI/hooks/useGenerativeTask';
import { GenerativeTaskStatusEnum, GenerativeTaskTypeEnum } from '@/types/schema';

export interface VideoSlideFromSelection {
  heygenTemplateId: string;
  slideTemplateId: string;
  name: string;
  transcript: string;
  variables: Record<string, string>;
}

interface UseVideoSlideSelectionProps {
  onSuccess: (slides: VideoSlideFromSelection[]) => void;
}

const VIDEO_SLIDE_SELECTION_DEFAULT_CATEGORY = 'default' as const;

export function useVideoSlideSelection(props: UseVideoSlideSelectionProps) {
  const { onSuccess } = props;
  const { showErrorSnackbar } = useSnackbar();
  const { t } = useTranslation('Course');
  const handleTaskError = useEvent((message?: string | null) => {
    showErrorSnackbar({
      message: message || t('Course:AiVideoGeneration.FailedToGenerateStoryboardSlides'),
    });
  });

  const onSuccessEvent = useEvent((generativeTask: GenerativeTaskFragment) => {
    const slides = generativeTask.result.videoSlideSelection?.slides;
    if (!slides || slides.length === 0) {
      showErrorSnackbar({ message: t('Course:AiVideoGeneration.NoSlidesWereGenerated') });
      return;
    }
    const adaptedSlides = slides.map((slide) => {
      const parsedVariables = slide.variables
        ? jsonParse<Record<string, string>>(slide.variables).data ?? {}
        : {};

      return {
        ...slide,
        variables: parsedVariables,
      };
    }) as VideoSlideFromSelection[];
    onSuccess(adaptedSlides);
  });

  const { executeCreateGenerativeTask, fetching, generativeTaskId, generativeTask } =
    useGenerativeTask({
      onSuccess: onSuccessEvent,
      autoCleanup: true,
      onTaskError: (error) => {
        handleTaskError(error?.message);
      },
      suppressDefaultErrorHandling: true,
    });

  const selectSlides = useEvent(
    ({
      videoScript,
      lessonContent,
      filePath,
      fileSummary,
    }: {
      videoScript: string;
      lessonContent: string;
      filePath?: string;
      fileSummary?: string;
    }) => {
      if (!videoScript.trim()) {
        showErrorSnackbar({
          message: t('Course:AiVideoGeneration.PleaseProvideAVideoScriptBeforeGeneratingSlides'),
        });
        return;
      }
      const normalizedFilePath = filePath?.trim() || undefined;
      const normalizedFileSummary = fileSummary?.trim() || undefined;
      executeCreateGenerativeTask({
        type: GenerativeTaskTypeEnum.VideoSlideSelection,
        input: {
          videoSlideSelection: {
            transcript: videoScript.trim(),
            lessonContent: lessonContent.trim(),
            ...(normalizedFilePath
              ? {
                  filePath: normalizedFilePath,
                  fileSummary: normalizedFileSummary,
                }
              : { category: VIDEO_SLIDE_SELECTION_DEFAULT_CATEGORY }),
          },
        },
      });
    },
  );

  const isSelectingSlides =
    fetching ||
    (!!generativeTaskId && !generativeTask) ||
    (generativeTask &&
      [GenerativeTaskStatusEnum.Processing, GenerativeTaskStatusEnum.ToBeProcessed].includes(
        generativeTask.status,
      ));

  return {
    selectSlides,
    isSelectingSlides,
  };
}
